import { useTranslation } from "@msuite/ui/localization";
import type { IncomingInvoiceCheckListItem } from "@msuite/utils/types";
import { Link } from "react-router";
import { formatInvoiceAmount } from "./format-invoice-values";

/** Props Interface */
interface IncomingInvoiceCheckHintProps {
	check: IncomingInvoiceCheckListItem;
	currency: string;
}

/** The reason a skipped check was skipped, per reason the server records. */
const SKIP_REASON_LABEL = {
	"no-positions": "incoming_invoices.check_hints.no_positions",
	"no-cost-centers": "incoming_invoices.check_hints.no_cost_centers",
	incomplete: "incoming_invoices.check_hints.incomplete",
	"invalid-iban": "incoming_invoices.check_hints.invalid_iban",
} as const;

/** Why `iban-known` failed: a first account for the contact, one beside its known ones, or a partner's. */
const IBAN_REASON_LABEL = {
	"new-iban": "incoming_invoices.check_hints.new_iban",
	"suspected-fraud": "incoming_invoices.check_hints.suspected_fraud",
	"unknown-partner-iban": "incoming_invoices.check_hints.unknown_partner_iban",
} as const;

/**
 * Why a check came out as it did, where the label alone does not say: the difference a sum is
 * off by, the earlier invoice a duplicate repeats, or the reason a check was skipped. A passed check, and one that failed for a missing
 * field its label already names, show nothing.
 */
export function IncomingInvoiceCheckHint({ check, currency }: IncomingInvoiceCheckHintProps) {
	/** Context */
	const { t } = useTranslation();

	/** Computed */
	const difference = check.details?.difference;
	const reason = check.details?.reason;
	const duplicateOfId = check.details?.duplicateOfId;

	/** Render */
	if (check.state === "failed" && duplicateOfId) {
		return (
			<Link
				to={`/controlling/incoming-invoices/${duplicateOfId}`}
				className="link label shrink-0"
				data-cy="incoming-invoice-duplicate-link"
			>
				{t(
					reason === "same-document"
						? "incoming_invoices.check_hints.same_document_as"
						: "incoming_invoices.check_hints.duplicate_of",
					{ number: check.details?.duplicateOfNumber ?? "–" },
				)}
			</Link>
		);
	}
	if (check.state === "failed" && typeof difference === "number") {
		return (
			<span className="label shrink-0">
				{t("incoming_invoices.check_hints.difference", {
					amount: formatInvoiceAmount(difference, currency),
				})}
			</span>
		);
	}
	if (check.state === "failed" && reason && reason in IBAN_REASON_LABEL) {
		return (
			<span
				className={
					reason !== "new-iban" ? "label shrink-0 text-red-500!" : "label shrink-0 text-amber-500!"
				}
			>
				{t(IBAN_REASON_LABEL[reason as keyof typeof IBAN_REASON_LABEL])}
			</span>
		);
	}
	if (check.state === "skipped" && reason && reason in SKIP_REASON_LABEL) {
		return (
			<span className="label shrink-0">
				{t(SKIP_REASON_LABEL[reason as keyof typeof SKIP_REASON_LABEL])}
			</span>
		);
	}
	return null;
}
